import React, { FormEvent, FunctionComponent, useCallback } from 'react';
import styled from 'styled-components';

import { SubmitButton } from 'components';
import { Heading, Text } from 'components/chroma';

import { toDisplayAddress } from 'scenes/common/helpers/address';
import {
  calculatePolicyExpiryDate,
  toAussieStandardDateFormat,
} from 'scenes/common/helpers/datetime';

import { MotorFullPageProps } from 'scenes/motor/types';
import {
  getQuoteNumber,
  getPolicyStartDate,
  getSelectedExcess,
  getContactEmailAddress,
  getOvernightParkingAddress,
  getVehicleDescription,
  getSelectedInsuranceOption,
  getExtras,
  getPreselectedPaymentFrequency,
  isMonthlyPaymentPreselected,
  getPreselectedPrice,
} from 'scenes/motor/selectors';

import { PriceWithFrequencySwitch } from 'scenes/common/components/QuoteInformation/QuotePriceCards/PriceWithFrequencySwitch';
import { DEFAULT_HEADING_SIZES } from 'scenes/common/helpers/fonts';
import { getAvailableExtras } from 'scenes/motor/helpers/OptionalExtras/getAvailableExtras';

import { SummaryContainer } from 'scenes/common/components/PolicySummary';
import { OverallDetailsSummary } from './OverallSummary';

const OverallDetailsContainer = styled.div`
  margin-top: 38px;
`;

export const SummaryStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onCompleteQuote,
  onEditQuoteDetails,
  onEditPolicyDetails,
  onEditCoverDetails,
  onEditDriver,
  referenceData,
  onSwitchPaymentFrequency,
}) => {
  const {
    insuranceType,
    coverType,
    premium: { price, annualPrice },
  } = getSelectedInsuranceOption(funnelState);
  const savedAmount = Number(price) - Number(annualPrice);
  const policyStartDate = getPolicyStartDate(funnelState);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      onCompleteQuote();
      event.preventDefault();
    },
    [onCompleteQuote]
  );

  return (
    <form onSubmit={onSubmit}>
      <Heading size={2} margin={{ top: 2 }} fontSize={DEFAULT_HEADING_SIZES}>
        Review your Car Insurance Summary
      </Heading>

      <SummaryContainer>
        <PriceWithFrequencySwitch
          paymentFrequency={getPreselectedPaymentFrequency(funnelState)}
          coverType={coverType}
          displayPrice={getPreselectedPrice(funnelState)}
          monthlyPriceSum={price}
          savedAmount={savedAmount}
          monthlyPaymentPreselected={isMonthlyPaymentPreselected(funnelState)}
          onSwitchPaymentFrequency={onSwitchPaymentFrequency}
        />

        <SubmitButton enabled>Next</SubmitButton>

        <Heading size={4} margin={{ top: 5 }}>
          Quote summary
        </Heading>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Policy No.
        </Heading>
        <Text>{getQuoteNumber(funnelState)}</Text>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Start date
        </Heading>
        <Text>{toAussieStandardDateFormat(policyStartDate, 'YYYY-MM-DD')}</Text>
        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          End date
        </Heading>
        <Text>
          {toAussieStandardDateFormat(
            calculatePolicyExpiryDate(policyStartDate),
            'YYYY-MM-DD'
          )}
        </Text>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Car details
        </Heading>
        <Text>{getVehicleDescription(funnelState)}</Text>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Where car is parked overnight
        </Heading>
        <Text>{toDisplayAddress(getOvernightParkingAddress(funnelState))}</Text>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Excess
        </Heading>
        <Text>${getSelectedExcess(funnelState).amount}</Text>

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Optional extras
        </Heading>
        {getAvailableExtras(insuranceType).map((extra) => (
          <Text key={extra.code} margin={{ bottom: 0 }}>
            {extra.label} -{' '}
            {getExtras(funnelState).includes(extra.code) ? 'YES' : 'NO'}
          </Text>
        ))}

        <Heading size={5} margin={{ top: 5, bottom: 0 }}>
          Email address
        </Heading>
        <Text margin={{ bottom: 0 }}>
          {getContactEmailAddress(funnelState)}
        </Text>
      </SummaryContainer>

      <OverallDetailsContainer>
        <OverallDetailsSummary
          funnelState={funnelState}
          onEditQuoteDetails={onEditQuoteDetails}
          onEditPolicyDetails={onEditPolicyDetails}
          onEditCoverDetails={onEditCoverDetails}
          onEditDriver={onEditDriver}
          referenceData={referenceData}
        />
      </OverallDetailsContainer>
      <SummaryContainer border={false}>
        <SubmitButton enabled>Next</SubmitButton>
      </SummaryContainer>
    </form>
  );
};

export default SummaryStep;
